import { SubWithVarietyDto } from 'common/api/dto/get-subVarieties.dto';
import {
  orgVarName,
  regVarName,
} from 'features/settings-view/hooks/useSubVarietiesTableData';
import { UseFormGetFieldState } from 'react-hook-form';
import { SubVarietiesValues } from '../Tabs/SubVarieties';

type SubVarietyKey = typeof regVarName | typeof orgVarName;

type SubVarFormField = EditSubVarDto & {
  isRegDirty: boolean;
  isOrgDirty: boolean;
};

export type AddSubVarDto = {
  code: string;
  regVarId: number | null;
  orgVarId: number | null;
};

export type EditSubVarDto = {
  id: number;
  regVarId: number | null;
  orgVarId: number | null;
};

/**
 * A helper function to create a key used by `useForm` to map the sub-variety
 * form value to the correct input.
 */
export const createSubVarietyKey = (
  id: number,
  varType: SubVarietyKey,
): string => `${id}_${varType}`;

/** The helper function to parse metadata from `useForm`'s dynamic keys. */
export const parseSubVarietyKey = (
  key: string,
): {
  id: number;
  varType: SubVarietyKey;
} => {
  const [id, varType] = key.split('_');

  return {
    id: parseInt(id, 10),
    varType: varType as SubVarietyKey,
  };
};

export const formatDefaultSubVars = (
  subVars: SubWithVarietyDto[],
): SubVarietiesValues => {
  return subVars.reduce((acc, curr) => {
    const { id, conventionalVar, organicVar } = curr;

    acc[createSubVarietyKey(id, regVarName)] = conventionalVar
      ? { label: conventionalVar.varietyCode, value: conventionalVar }
      : null;
    acc[createSubVarietyKey(id, orgVarName)] = organicVar
      ? { label: organicVar.varietyCode, value: organicVar }
      : null;

    return acc;
  }, {} as SubVarietiesValues);
};

export const toSubVarietiesDtos = (
  data: SubVarietiesValues,
  getFieldState: UseFormGetFieldState<SubVarietiesValues>,
): EditSubVarDto[] =>
  Object.entries(data)
    .reduce<SubVarFormField[]>((acc, [key, input]) => {
      const { id, varType } = parseSubVarietyKey(key);
      const subVarIdx = acc.findIndex(sub => sub.id === id);
      const { isDirty } = getFieldState(key);

      // If the sub-variety already exists, update it with second variety entry.
      if (subVarIdx > -1) {
        acc[subVarIdx] = {
          ...acc[subVarIdx],
          ...(varType === regVarName && {
            regVarId: input?.value ? input.value.id : null,
            isRegDirty: isDirty,
          }),
          ...(varType === orgVarName && {
            orgVarId: input?.value ? input.value.id : null,
            isOrgDirty: isDirty,
          }),
        };

        const regDirty = acc[subVarIdx].isRegDirty;
        const orgDirty = acc[subVarIdx].isOrgDirty;
        const hasDirtyField =
          (regDirty && !orgDirty) ||
          (orgDirty && !regDirty) ||
          (regDirty && orgDirty);

        // If both fields are unchanged, do not add the sub-variety to DTO list.
        if (!hasDirtyField) {
          acc.splice(subVarIdx, 1);
        }
      } else {
        acc.push({
          id,
          ...(varType === regVarName && {
            regVarId: input?.value ? input.value.id : null,
            orgVarId: null,
            isRegDirty: isDirty,
            isOrgDirty: false,
          }),
          ...(varType === orgVarName && {
            orgVarId: input?.value ? input.value.id : null,
            regVarId: null,
            isOrgDirty: isDirty,
            isRegDirty: false,
          }),
        } as SubVarFormField);
      }
      return acc;
    }, [])
    .map(({ id, regVarId, orgVarId }) => ({
      id,
      regVarId,
      orgVarId,
    }));
